<template>
  <ul class="divide-y divide-gray-200">
    <li>
      <button
        type="button"
        class="block hover:bg-gray-50 cursor-pointer relative text-left w-full"
        @click="toggleSyncStore = !toggleSyncStore"
      >
        <span
          v-if="toggleSyncStore && env.onboarding_platform"
          class="w-full h-full absolute bg-green-50 shadow-md"
        />
        <span
          v-else-if="toggleSyncStore && !env.onboarding_platform"
          class="w-full h-full absolute bg-gray-50 shadow-md"
        />
        <div class="flex items-center pr-6">
          <div class="min-w-0 flex-1 flex items-center justify-between">
            <div class="relative flex space-x-3">
              <div class="flex-shrink-0 text-white flex w-[59px] min-w-[64px] items-center justify-center">
                <div
                  v-if="!env.onboarding_platform"
                  class="bg-gray-400 w-full flex h-full items-center justify-center"
                >
                  <StatusIcon />
                </div>
                <div
                  v-else
                  class="bg-green-400 w-full flex h-full items-center justify-center"
                >
                  <StatusIcon :status-color="env.onboarding_platform ? 'green' : ''" />
                </div>
              </div>
              <div class="p-4">
                <p class="text-sm font-medium text-gray-600 truncate">
                  Connect your
                  <span v-if="env.platform === 'bigcommerce'">
                    BigCommerce
                  </span>
                  <span
                    v-else-if="env.environment === 'live' && (env.platform === 'shopify_plus' || env.platform === 'shopify')"
                  >
                    Shopify
                  </span>
                  <span
                    v-else
                  >
                    eCommerce
                  </span>
                  store
                </p>
                <p class="mt-1 items-center text-sm text-gray-500">
                  <span
                    v-if="!env.onboarding_platform"
                  >
                    Select where you want to install SparkLayer
                  </span>
                  <span
                    v-else
                  >
                    Your website is successfully connected to SparkLayer!
                  </span>
                </p>
              </div>
            </div>
            <div class="relative hidden md:flex mr-6">
              <OnboardingStatusText :status="env.onboarding_platform ? 'Completed' : 'Pending'" />
            </div>
          </div>
          <div class="z-10">
            <ChevronDownIcon
              v-if="toggleSyncStore"
              class="h-5 w-5 text-gray-400"
            />
            <ChevronRightIcon
              v-else
              class="h-5 w-5 text-gray-400"
            />
          </div>
        </div>
      </button>
      <div
        v-if="toggleSyncStore"
        ref="connect"
        class="px-8 pb-8 -ml-1 sm:px-20"
      >
        <div
          v-if="!env.onboarding_platform"
          class="mt-6"
        >
          <p
            class="mt-2 text-sm text-gray-500"
          >
            You need to connect your website to SparkLayer before you can begin the onboarding
            process. It only takes a moment!
          </p>
          <div class="mt-4 -ml-3">
            <router-link
              to="/configuration/integrations/platform"
              class="w-full inline-flex justify-center items-center px-4 py-2 border text-sm font-medium rounded-md sm:ml-3 sm:w-auto sm:text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500 shadow-sm"
            >
              Connect your website
            </router-link>
          </div>
        </div>
        <div
          v-else
        >
          <div class="-ml-4 mb-6 flex justify-between items-center flex-wrap sm:flex-nowrap">
            <div class="ml-4 mt-4">
              <p
                class="mt-3 text-sm text-gray-500"
              >
                SparkLayer automatically connects to your website to synchronise product and
                customer data.
                <a
                  href="https://docs.sparklayer.io/dashboard-configurations#JJHwx"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                >
                  <span>Learn more</span>
                  <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                </a>
              </p>
            </div>
          </div>
          <div class="align-middle min-w-full overflow-hidden overflow-x-auto shadow sm:rounded-lg">
            <table class="min-w-full divide-y divide-gray-200 sm:rounded-lg">
              <thead>
                <tr>
                  <th class="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data feed
                  </th>
                  <th class="max-w-sm px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th class="max-w-sm px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <SyncData data-type="products" />
                <SyncData data-type="customers" />
              </tbody>
            </table>
          </div>
          <div
            class="mt-4 text-xs text-gray-500 block sm:flex justify-between"
          >
            Please note, data will still be synchronising even if you see errors.
          </div>
          <div class="mt-4 -ml-3">
            <router-link
              to="/configuration/integrations/platform"
              class="w-full inline-flex justify-center items-center px-4 py-2 border text-sm font-medium rounded-md sm:ml-3 sm:w-auto sm:text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 text-gray-700 bg-white hover:bg-gray-50 focus:ring-indigo-500 border-gray-300 shadow-sm"
            >
              View or update connected website
            </router-link>
          </div>
        </div>
      </div>
    </li>
    <li>
      <button
        type="button"
        class="block hover:bg-gray-50 cursor-pointer relative text-left w-full"
        @click="toggleFrontend = !toggleFrontend"
      >
        <span
          v-if="toggleFrontend && env.onboarding_frontend"
          class="w-full h-full absolute bg-green-50 shadow-md"
        />
        <span
          v-else-if="toggleFrontend && !env.onboarding_frontend"
          class="w-full h-full absolute bg-gray-50 shadow-md"
        />
        <div class="flex items-center pr-6">
          <div class="min-w-0 flex-1 flex items-center">
            <div class="relative flex space-x-3">
              <div class="flex-shrink-0 text-white flex w-[59px] min-w-[64px] items-center justify-center">
                <div
                  v-if="!env.onboarding_frontend"
                  class="bg-gray-400 w-full flex h-full items-center justify-center"
                >
                  <StatusIcon />
                </div>
                <div
                  v-else
                  class="bg-green-400 w-full flex h-full items-center justify-center"
                >
                  <StatusIcon :status-color="env.onboarding_frontend ? 'green' : ''" />
                </div>
              </div>
              <div class="p-4">
                <p class="text-sm font-medium text-gray-600">
                  Enable the storefront B2B widgets
                </p>
                <p class="mt-1 flex items-center text-sm text-gray-500">
                  <span
                    v-if="!env.onboarding_frontend"
                  >
                    Activate and customise the SparkLayer Frontend on your website
                  </span>
                  <span
                    v-else
                  >
                    It looks like you've enabled the SparkLayer frontend widgets!
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div class="relative hidden md:flex mr-6">
            <OnboardingStatusText :status="env.onboarding_frontend ? 'Completed' : 'Pending'" />
          </div>
          <div class="z-10">
            <ChevronDownIcon
              v-if="toggleFrontend"
              class="h-5 w-5 text-gray-400"
            />
            <ChevronRightIcon
              v-else
              class="h-5 w-5 text-gray-400"
            />
          </div>
        </div>
      </button>
      <div
        v-if="toggleFrontend"
        ref="widgets"
        class="px-8 pb-6 -ml-1 sm:px-20"
      >
        <div class="-ml-4 mb-0 flex justify-between items-center flex-wrap sm:flex-nowrap">
          <div class="ml-4 mt-4">
            <p class="mt-3 mb-2 text-sm text-gray-500">
              To allow B2B customers to place orders, you'll need to install our special
              <router-link
                to="/configuration/frontend/core"
                class="font-medium text-indigo-600 hover:text-indigo-500"
              >
                storefront widgets
              </router-link> onto your website.
              <a
                href="https://docs.sparklayer.io/frontend-integration"
                target="_blank"
                rel="noopener noreferrer"
                class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
              >
                <span>Learn more</span>
                <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
              </a>
            </p>
            <FrontendHints
              v-if="env.environment === 'live' && (env.platform === 'shopify_plus' || env.platform === 'shopify')"
              :hide-onboarding-hint="env.onboarding_frontend"
            />
            <div class="bg-yellow-50 shadow sm:rounded-lg px-4 py-5 sm:p-6 my-6 max-w-3xl">
              <fieldset>
                <h3 class="text-m leading-6 font-medium text-gray-900">
                  ✅ Onboarding Checklist
                </h3>
                <p class="mt-3 mb-4 text-sm text-gray-500">
                  Please tick the items below as you complete them:
                </p>
                <div class="mt-4">
                  <label class="relative flex items-start mt-4 cursor-pointer">
                    <input
                      id="installFlagsCore"
                      v-model="installFlags.core"
                      type="checkbox"
                      class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                    >
                    <div class="ml-3 text-sm">
                      <p class="font-medium text-gray-700">
                        You've added the
                        <router-link
                          to="/configuration/frontend/core"
                          class="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          SparkLayer core script
                        </router-link>
                        to your website header
                      </p>
                      <p class="text-gray-500 text-xs">
                        The core script loads special JavaScript that displays the SparkLayer
                        interfaces.
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href="https://docs.sparklayer.io/frontend-integration#5YjYK"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                        >
                          <span>Learn more</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                      </p>
                    </div>
                  </label>
                </div>
                <div class="mt-4">
                  <label class="relative flex items-start mt-4 cursor-pointer">
                    <input
                      id="installFlagsPdp"
                      v-model="installFlags.pdp"
                      type="checkbox"
                      class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                    >
                    <div class="ml-3 text-sm">
                      <p class="font-medium text-gray-700">
                        You've added the
                        <router-link
                          to="/configuration/frontend/pdp"
                          class="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          product page scripts
                        </router-link>
                        to your product page(s)
                      </p>
                      <p class="text-gray-500 text-xs">
                        For logged in B2B customers, this updates the standard purchasing interface
                        with SparkLayer.
                        <a
                          href="https://docs.sparklayer.io/frontend-integration#aHYmA"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                        >
                          <span>Learn more</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                      </p>
                    </div>
                  </label>
                </div>
                <div class="mt-4">
                  <label class="relative flex items-start mt-4 cursor-pointer">
                    <input
                      id="installFlagsCss"
                      v-model="installFlags.css"
                      type="checkbox"
                      class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                    >
                    <div class="ml-3 text-sm">
                      <p class="font-medium text-gray-700">
                        You've added
                        <router-link
                          to="/configuration/frontend/css"
                          class="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          CSS overwrites
                        </router-link>
                        to match your website's look and feel
                      </p>
                      <p class="text-gray-500 text-xs">
                        CSS overwrites let you modify colours, typefaces, spacing, and more.
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href="https://docs.sparklayer.io/customising-design"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                        >
                          <span>Learn more</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                      </p>
                    </div>
                  </label>
                </div>
                <div class="mt-4">
                  <label class="relative flex items-start mt-4 cursor-pointer">
                    <input
                      id="installFlagsCheckout"
                      v-model="installFlags.checkout"
                      type="checkbox"
                      class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                    >
                    <div class="ml-3 text-sm">
                      <p class="font-medium text-gray-700">
                        You've added the
                        <router-link
                          to="/configuration/frontend/checkout"
                          class="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          checkout script
                        </router-link>
                        to your
                        <a
                          v-if="shopifyDomainSettings"
                          :href="shopifyDomainSettings"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span>Shopify admin</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                        <a
                          v-else
                          rel="noopener noreferrer"
                        >
                          <span>website admin</span>
                        </a>
                      </p>
                      <p class="text-gray-500 text-xs">
                        The checkout script tells SparkLayer when an order has successfully been
                        placed on your website.
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href="https://docs.sparklayer.io/frontend-integration"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                        >
                          <span>Learn more</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                      </p>
                    </div>
                  </label>
                </div>
                <div class="mt-4">
                  <label class="relative flex items-start mt-4 cursor-pointer">
                    <input
                      id="installFlagsAdvanced"
                      v-model="installFlags.advanced"
                      type="checkbox"
                      class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                    >
                    <div class="ml-3 text-sm">
                      <p class="font-medium text-gray-700">
                        You've reviewed optional
                        <router-link
                          to="/configuration/frontend/tips"
                          class="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          advanced configurations
                        </router-link>
                      </p>
                      <p class="text-gray-500 text-xs">
                        Advanced configurations include settings up pack sizing, retail prices,
                        automations and more.
                        <a
                          target="_blank"
                          href="https://docs.sparklayer.io/configurations"
                          rel="noopener noreferrer"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                        >
                          <span>Learn more</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                      </p>
                    </div>
                  </label>
                </div>
                <div class="mt-3 text-sm text-gray-500">
                  👉 You can also view our more in-depth
                  <a
                    target="_blank"
                    href="https://docs.sparklayer.io/frontend-integration#Q75Ee"
                    rel="noopener noreferrer"
                    class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                  >
                    <span>frontend checklist</span>
                    <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                  </a>
                </div>
              </fieldset>
            </div>
            <div
              v-if="env.platform === 'shopify' || env.platform === 'shopify_plus'"
              class="mt-4 mb-4 text-xs text-gray-500 block sm:flex justify-between"
            >
              Please note, if you're using the Shopify B2B Dawn Theme, the code changes are
              automatically applied and you can mark these steps as complete.
            </div>
            <Btn
              v-if="!env.onboarding_frontend"
              :disabled="!installFlagsAllow"
              :loading="installLoading"
              class="-ml-3"
              @click="toggleInstallChecks"
            >
              I've installed the B2B widgets
            </Btn>
            <Btn
              v-if="env.onboarding_frontend"
              button-style="secondary"
              class="-ml-3"
              :loading="installLoading"
              @click="toggleInstallChecks"
            >
              Mark as not complete
            </Btn>
          </div>
        </div>
      </div>
    </li>
    <li>
      <button
        type="button"
        class="block hover:bg-gray-50 cursor-pointer relative text-left w-full"
        @click="toggleSyncData = !toggleSyncData"
      >
        <span
          v-if="toggleSyncData && env.onboarding_sync"
          class="w-full h-full absolute bg-green-50 shadow-md"
        />
        <span
          v-else-if="toggleSyncData && !env.onboarding_sync"
          class="w-full h-full absolute bg-gray-50 shadow-md"
        />
        <div class="flex items-center pr-6">
          <div class="min-w-0 flex-1 flex items-center justify-between">
            <div class="relative flex space-x-3">
              <div class="flex-shrink-0 text-white flex w-[59px] min-w-[64px] items-center justify-center">
                <div
                  v-if="!env.onboarding_sync"
                  class="bg-gray-400 w-full flex h-full items-center justify-center"
                >
                  <StatusIcon />
                </div>
                <div
                  v-else
                  class="bg-green-400 w-full flex h-full items-center justify-center"
                >
                  <StatusIcon :status-color="env.onboarding_sync ? 'green' : ''" />
                </div>
              </div>
              <div class="p-4">
                <p class="text-sm font-medium text-gray-600 truncate">
                  Upload your B2B pricing
                </p>
                <p class="mt-1 flex pr-9 md:pr-0 items-center text-sm text-gray-500">
                  <span
                    v-if="!env.onboarding_sync"
                  >
                    Upload your price lists for your B2B customers
                  </span>
                  <span
                    v-else
                  >
                    It looks like you've uploaded your B2B pricing!
                  </span>
                </p>
              </div>
            </div>
            <div class="relative hidden md:flex mr-6">
              <OnboardingStatusText :status="env.onboarding_sync ? 'Completed' : 'Pending'" />
            </div>
          </div>
          <div class="z-10">
            <ChevronDownIcon
              v-if="toggleSyncData"
              class="h-5 w-5 text-gray-400"
            />
            <ChevronRightIcon
              v-else
              class="h-5 w-5 text-gray-400"
            />
          </div>
        </div>
      </button>
      <div
        v-if="toggleSyncData"
        ref="pricing"
        class="px-8 pb-10 -ml-1 sm:px-20"
      >
        <div class="-ml-4 mb-6 flex justify-between items-center flex-wrap sm:flex-nowrap">
          <div class="ml-4 mt-4">
            <p
              class="mt-3 text-sm text-gray-500"
            >
              To setup your B2B pricing, you'll need to create a price list.
              <router-link
                to="/products"
                class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
              >
                <span>Get started</span>
                <ArrowRightIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
              </router-link>
            </p>
          </div>
        </div>
        <div class="bg-yellow-50 shadow sm:rounded-lg px-4 py-5 sm:p-6 my-6 max-w-3xl">
          <fieldset>
            <h3 class="text-m leading-6 font-medium text-gray-900">
              ✅  Onboarding Checklist
            </h3>
            <p class="mt-3 mb-4 text-sm text-gray-500">
              Please tick the items below as you complete them:
            </p>
            <div class="mt-4">
              <label class="relative flex items-start mt-4 cursor-pointer">
                <input
                  id="pricesFlagsOne"
                  v-model="pricingFlags.one"
                  type="checkbox"
                  class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                >
                <div class="ml-3 text-sm">
                  <p class="font-medium text-gray-700">
                    You've created your
                    <router-link
                      to="/products/price-lists"
                      class="font-medium text-indigo-600 hover:text-indigo-500"
                    >
                      first price list
                    </router-link>
                    in SparkLayer
                  </p>
                  <p class="text-gray-500 text-xs">
                    You've followed the steps to set up either an automated or manual price list
                    <a
                      href="https://docs.sparklayer.io/managing-pricing"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                    >
                      <span>Learn more</span>
                      <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                    </a>
                  </p>
                </div>
              </label>
            </div>
            <div class="mt-4">
              <label class="relative flex items-start mt-4 cursor-pointer">
                <input
                  id="pricingFlagsTwo"
                  v-model="pricingFlags.two"
                  type="checkbox"
                  class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                >
                <div class="ml-3 text-sm">
                  <p class="font-medium text-gray-700">
                    You've optionally reviewed more advanced pricing, e.g.
                    quantity pricing and pack sizes
                  </p>
                  <p class="text-gray-500 text-xs">
                    You've reviewed how quantity pricing and pricing rules such pack sizing work
                    <a
                      href="https://docs.sparklayer.io/quantity-pricing"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                    >
                      <span>Learn more</span>
                      <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                    </a>
                  </p>
                </div>
              </label>
            </div>
          </fieldset>
        </div>
        <div
          class="mt-4 text-xs text-gray-500 block"
        >
          Are you having issues uploading your B2B pricing?  See our
          <a
            href="https://docs.sparklayer.io/troubleshooting"
            target="_blank"
            rel="noopener noreferrer"
            class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
          >
            <span>troubleshooting guide</span>
            <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
          </a>
        </div>
        <div class="mt-4 -ml-3">
          <Btn
            v-if="!env.onboarding_sync"
            :disabled="!pricingFlagsAllow"
            :loading="pricingLoading"
            @click="toggleSyncFlag"
          >
            I've uploaded my pricing
          </Btn>
          <Btn
            v-if="env.onboarding_sync"
            button-style="secondary"
            :loading="pricingLoading"
            @click="toggleSyncFlag"
          >
            Mark as not complete
          </Btn>
        </div>
      </div>
    </li>
    <li>
      <button
        type="button"
        class="block hover:bg-gray-50 cursor-pointer relative text-left w-full"
        @click="toggleSettings = !toggleSettings"
      >
        <span
          v-if="toggleSettings && env.onboarding_settings"
          class="w-full h-full absolute bg-green-50 shadow-md"
        />
        <span
          v-else-if="toggleSettings && !env.onboarding_settings"
          class="w-full h-full absolute bg-gray-50 shadow-md"
        />
        <div class="flex items-center pr-6">
          <div class="min-w-0 flex-1 flex items-center">
            <div class="relative flex space-x-3">
              <div class="flex-shrink-0 text-white flex w-[59px] min-w-[64px] items-center justify-center">
                <div
                  v-if="!env.onboarding_settings"
                  class="bg-gray-400 w-full flex h-full items-center justify-center"
                >
                  <StatusIcon />
                </div>
                <div
                  v-else
                  class="bg-green-400 w-full flex h-full items-center justify-center"
                >
                  <StatusIcon :status-color="env.onboarding_settings ? 'green' : ''" />
                </div>
              </div>
              <div class="p-4">
                <p class="text-sm font-medium text-gray-600">
                  Set up your B2B customer groups
                </p>
                <p class="mt-1 flex  items-center text-sm text-gray-500">
                  <span
                    v-if="!env.onboarding_settings"
                  >
                    Configure which prices and payment methods B2B customers see
                  </span>
                  <span
                    v-else
                  >
                    It looks like you've configured your B2B customer groups!
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div class="relative hidden md:flex mr-6">
            <OnboardingStatusText :status="env.onboarding_settings ? 'Completed' : 'Pending'" />
          </div>
          <div class="z-10">
            <ChevronDownIcon
              v-if="toggleSettings"
              class="h-5 w-5 text-gray-400"
            />
            <ChevronRightIcon
              v-else
              class="h-5 w-5 text-gray-400"
            />
          </div>
        </div>
      </button>
      <div
        v-if="toggleSettings"
        ref="customer-groups"
        class="px-8 pb-6 -ml-1 sm:px-20"
      >
        <div class="-ml-4 mb-0 flex justify-between items-center flex-wrap sm:flex-nowrap">
          <div class="ml-4 mt-4">
            <p class="mt-3 mb-4 text-sm text-gray-500">
              <router-link
                to="/customers/groups"
                class="font-medium text-indigo-600 hover:text-indigo-500"
              >
                Customer groups
              </router-link>
              let you set which price lists and which payment methods your B2B customers can use.
              <router-link
                to="/customers/groups"
                class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
              >
                <span>Get started</span>
                <ArrowRightIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
              </router-link>
            </p>
            <div class="bg-yellow-50 shadow sm:rounded-lg px-4 py-5 sm:p-6 my-6 max-w-3xl">
              <fieldset>
                <h3 class="text-m leading-6 font-medium text-gray-900">
                  ✅  Onboarding Checklist
                </h3>
                <p class="mt-3 mb-4 text-sm text-gray-500">
                  Please tick the items below as you complete them:
                </p>
                <div class="mt-4">
                  <label class="relative flex items-start mt-4 cursor-pointer">
                    <input
                      id="settingsFlagsOne"
                      v-model="settingsFlags.one"
                      type="checkbox"
                      class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                    >
                    <div class="ml-3 text-sm">
                      <p class="font-medium text-gray-700">
                        You have at least one
                        <router-link
                          to="/customers/groups"
                          class="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          B2B customer group
                        </router-link>
                        set up in SparkLayer
                      </p>
                      <p class="text-gray-500 text-xs">
                        This includes a minimum order value, available payment method(s) and an
                        associated price list
                      </p>
                    </div>
                  </label>
                </div>
                <div class="mt-4">
                  <label class="relative flex items-start mt-4 cursor-pointer">
                    <input
                      id="settingsFlagsTwo"
                      v-model="settingsFlags.two"
                      type="checkbox"
                      class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                    >
                    <div class="ml-3 text-sm">
                      <p class="font-medium text-gray-700">
                        You have at least one customer set up in your
                        <a
                          v-if="shopifyDomainCustomers"
                          :href="shopifyDomainCustomers"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span>Shopify admin</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                      </p>
                      <p class="text-gray-500 text-xs">
                        You've created an example customer in your website admin
                        or you're using an existing customer account.
                        <a
                          href="https://docs.sparklayer.io/customers#HIB-b"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                        >
                          <span>Learn more</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                      </p>
                    </div>
                  </label>
                </div>
                <div class="mt-4">
                  <label class="relative flex items-start mt-4 cursor-pointer">
                    <input
                      id="settingsFlagsThree"
                      v-model="settingsFlags.three"
                      type="checkbox"
                      class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                    >
                    <div class="ml-3 text-sm">
                      <p class="font-medium text-gray-700">You have a
                        <a
                          v-if="shopifyDomainCustomers"
                          :href="shopifyDomainCustomers"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span>customer in Shopify</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                        that you can log in with
                      </p>
                      <p class="text-gray-500 text-xs">
                        You've a customer with the correct tag(s) to match those in SparkLayer
                        (e.g. b2b).
                        <a
                          href="https://docs.sparklayer.io/customers#yGzhS"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                        >
                          <span>Learn more</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                      </p>
                    </div>
                  </label>
                </div>
              </fieldset>
            </div>
            <Btn
              v-if="!env.onboarding_settings"
              :disabled="!settingsFlagsAllow"
              :loading="settingsLoading"
              class="-ml-3"
              @click="toggleSettingsChecks"
            >
              I've set my customer groups
            </Btn>
            <Btn
              v-if="env.onboarding_settings"
              button-style="secondary"
              class="-ml-3"
              :loading="settingsLoading"
              @click="toggleSettingsChecks"
            >
              Mark as not complete
            </Btn>
          </div>
        </div>
      </div>
    </li>
    <li>
      <button
        type="button"
        class="block hover:bg-gray-50 cursor-pointer relative text-left w-full"
        @click="toggleLaunch = !toggleLaunch"
      >
        <span
          v-if="toggleLaunch && env.onboarding_launch"
          class="w-full h-full absolute bg-green-50 shadow-md"
        />
        <span
          v-else-if="toggleLaunch && !env.onboarding_launch"
          class="w-full h-full absolute bg-gray-50 shadow-md"
        />
        <div class="flex items-center pr-6">
          <div class="min-w-0 flex-1 flex items-center">
            <div class="relative flex space-x-3">
              <div class="flex-shrink-0 text-white flex w-[59px] min-w-[64px] items-center justify-center">
                <div
                  v-if="!env.onboarding_launch"
                  class="bg-gray-400 w-full flex h-full items-center justify-center"
                >
                  <StatusIcon />
                </div>
                <div
                  v-else
                  class="bg-green-400 w-full flex h-full items-center justify-center"
                >
                  <StatusIcon :status-color="env.onboarding_launch ? 'green' : ''" />
                </div>
              </div>
              <div class="p-4">
                <p class="text-sm font-medium text-gray-600 truncate">
                  Test & Launch SparkLayer!
                </p>
                <p class="mt-1 flex pr-9 md:pr-0 items-center text-sm text-gray-500">
                  <span
                    v-if="!env.onboarding_launch"
                  >
                    Perform some final tests and checks before you start receiving B2B orders
                  </span>
                  <span
                    v-else
                  >
                    It looks like you've completed your tests!
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div class="relative hidden md:flex mr-6">
            <OnboardingStatusText :status="env.onboarding_launch ? 'Completed' : 'Pending'" />
          </div>
          <div class="z-10">
            <ChevronDownIcon
              v-if="toggleLaunch"
              class="h-5 w-5 text-gray-400"
            />
            <ChevronRightIcon
              v-else
              class="h-5 w-5 text-gray-400"
            />
          </div>
        </div>
      </button>
      <div
        v-if="toggleLaunch"
        ref="launch"
        class="px-8 pb-6 -ml-1 sm:px-20"
      >
        <div class="-ml-4 mb-0 flex justify-between items-center ">
          <div class="ml-4 mt-4">
            <p class="mt-3 mb-4 text-sm text-gray-500">
              Once you've completed the steps above, we just need you to run some final tests before
              you're ready to launch your B2B website!
            </p>
            <div class="bg-yellow-50 shadow sm:rounded-lg px-4 py-5 sm:p-6 my-6 max-w-3xl">
              <fieldset>
                <h3 class="text-m leading-6 font-medium text-gray-900">
                  ✅ Onboarding Checklist
                </h3>
                <p class="mt-3 mb-4 text-sm text-gray-500">
                  Please tick the items below as you complete them:
                </p>
                <div class="mt-4">
                  <label class="relative flex items-start mt-4 cursor-pointer">
                    <input
                      id="launchFlagsStart"
                      v-model="launchFlags.start"
                      type="checkbox"
                      class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                    >
                    <div class="ml-3 text-sm">
                      <p class="font-medium text-gray-700">
                        You've ensured your website has the
                        <a
                          v-if="shopifyDomainCheckout"
                          :href="shopifyDomainCheckout"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span>correct configurations</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                      </p>
                      <p class="text-gray-500 text-xs">
                        This includes ensuring customer accounts are enabled and you have payment
                        methods available.
                        <a
                          href="https://docs.sparklayer.io/installing-shopify-sparklayer#eQ6hB"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                        >
                          <span>Learn more</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                      </p>
                    </div>
                  </label>
                </div>
                <div class="mt-4">
                  <label class="relative flex items-start mt-4 cursor-pointer">
                    <input
                      id="launchFlagsLogin"
                      v-model="launchFlags.login"
                      type="checkbox"
                      class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                    >
                    <div class="ml-3 text-sm">
                      <p class="font-medium text-gray-700">
                        You can
                        <a
                          v-if="shopifyDomainAccount"
                          :href="shopifyDomainAccount"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span>login to your store</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                        as a B2B customer
                      </p>
                      <p class="text-gray-500 text-xs">
                        You're able to login and it displays the
                        <a
                          href="https://docs.sparklayer.io/my-account-interface"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                        >
                          <span>SparkLayer My Account interface</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                      </p>
                    </div>
                  </label>
                </div>
                <div class="mt-4">
                  <label class="relative flex items-start mt-4 cursor-pointer">
                    <input
                      id="launchFlagsPricing"
                      v-model="launchFlags.pricing"
                      type="checkbox"
                      class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                    >
                    <div class="ml-3 text-sm">
                      <p class="font-medium text-gray-700">
                        Your product pages are displaying the correct B2B prices
                      </p>
                      <p class="text-gray-500 text-xs">
                        Once logged in as a B2B customers, the prices you see
                        match those you've uploaded.
                        <a
                          href="https://docs.sparklayer.io/troubleshooting"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                        >
                          <span>Troubleshoot pricing issues</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                      </p>
                    </div>
                  </label>
                </div>
                <div class="mt-4">
                  <label class="relative flex items-start mt-4 cursor-pointer">
                    <input
                      id="launchFlagsCart"
                      v-model="launchFlags.cart"
                      type="checkbox"
                      class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                    >
                    <div class="ml-3 text-sm">
                      <p class="font-medium text-gray-700">
                        You can add products to an order
                      </p>
                      <p class="text-gray-500 text-xs">
                        Clicking 'Add to order' on a product page displays the
                        <a
                          href="https://docs.sparklayer.io/my-order-interface"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                        >
                          <span>SparkLayer Quick Order Interface</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                      </p>
                    </div>
                  </label>
                </div>
                <div class="mt-4">
                  <label class="relative flex items-start mt-4 cursor-pointer">
                    <input
                      id="launchFlagsCheckout"
                      v-model="launchFlags.checkout"
                      type="checkbox"
                      class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                    >
                    <div class="ml-3 text-sm">
                      <p class="font-medium text-gray-700">
                        You can go through the checkout and complete an order
                      </p>
                      <p class="text-gray-500 text-xs">
                        You can choose a payment method (e.g. "Pay by Invoice") and complete a test
                        order
                      </p>
                    </div>
                  </label>
                </div>
                <div class="mt-4">
                  <label class="relative flex items-start mt-4 cursor-pointer">
                    <input
                      id="launchFlagsOrders"
                      v-model="launchFlags.orders"
                      type="checkbox"
                      class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                    >
                    <div class="ml-3 text-sm">
                      <p class="font-medium text-gray-700">
                        Your order successfully appears in your
                        <a
                          v-if="shopifyDomainDraftOrders"
                          :href="shopifyDomainDraftOrders"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span>Shopify Draft Orders admin</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                      </p>
                      <p class="text-gray-500 text-xs">
                        Once you've placed an order, you can view this in your
                        website order admin. <br>You can
                        <router-link
                          to="/configuration/settings/general"
                          class="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          automatically convert
                        </router-link>
                        Draft orders into Completed orders.
                      </p>
                    </div>
                  </label>
                </div>
                <div class="mt-4">
                  <label class="relative flex items-start mt-4 cursor-pointer">
                    <input
                      id="launchFlagsInvite"
                      v-model="launchFlags.invite"
                      type="checkbox"
                      class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer mt-1"
                    >
                    <div class="ml-3 text-sm">
                      <p class="font-medium text-gray-700">
                        You're familiar with how to
                        <a
                          href="https://docs.sparklayer.io/customers#HIB-b"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span>invite your B2B customers</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                      </p>
                      <p class="text-gray-500 text-xs">
                        You've read our guide on
                        <a
                          href="https://docs.sparklayer.io/customers#HIB-b"
                          class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span>how to invite customers</span>
                          <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                        </a>
                        to your website so they can begin placing orders.
                      </p>
                    </div>
                  </label>
                </div>
              </fieldset>
            </div>
            <Btn
              v-if="!env.onboarding_launch"
              :disabled="!launchFlagsAllow"
              :loading="launchLoading"
              class="-ml-3"
              @click="toggleLaunchChecks"
            >
              I've completed the launch checks
            </Btn>
            <Btn
              v-if="env.onboarding_launch"
              button-style="secondary"
              class="-ml-3"
              :loading="launchLoading"
              @click="toggleLaunchChecks"
            >
              Mark as not complete
            </Btn>
          </div>
        </div>
      </div>
    </li>
    <li
      v-if="env.platform === 'shopify' || env.platform === 'shopify_plus'"
      class="bg-gray-50 flex items-center px-4 py-4 sm:px-6 rounded-b-md"
    >
      <div class="relative flex space-x-3 items-center">
        <div class="flex-shrink-0 mr-1.5 h-8 w-8 text-gray-400 justify-center flex">
          <svg
            v-if="env.platform === 'shopify'"
            width="25"
            height="28"
            viewBox="0 0 110 125"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M95.9 23.9C95.8 23.3 95.3 22.9 94.8 22.9C94.3 22.9 85.5 22.7 85.5 22.7C85.5 22.7 78.1 15.5 77.4 14.8C76.7 14.1 75.2 14.3 74.7 14.5C74.7 14.5 73.3 14.9 71 15.6C70.6 14.3 70 12.8 69.2 11.2C66.6 6.2 62.7 3.5 58.1 3.5C57.8 3.5 57.5 3.5 57.1 3.6C57 3.4 56.8 3.3 56.7 3.1C54.7 0.900003 52.1 -0.0999966 49 3.40026e-06C43 0.200003 37 4.5 32.2 12.2C28.8 17.6 26.2 24.4 25.4 29.7C18.5 31.8 13.7 33.3 13.6 33.4C10.1 34.5 10 34.6 9.60001 37.9C9.30001 40.4 0.100006 110.9 0.100006 110.9L76.5 124.1L109.6 115.9C109.5 115.8 96 24.5 95.9 23.9ZM67.2 16.8C65.4 17.3 63.4 18 61.3 18.6C61.3 15.6 60.9 11.3 59.5 7.7C64 8.6 66.2 13.7 67.2 16.8ZM57.2 19.9C53.2 21.1 48.8 22.5 44.4 23.8C45.6 19.1 48 14.4 50.8 11.3C51.9 10.2 53.4 8.9 55.1 8.1C56.9 11.6 57.3 16.5 57.2 19.9ZM49.1 4C50.5 4 51.7 4.3 52.7 4.9C51.1 5.8 49.5 7 48 8.6C44.2 12.7 41.3 19.1 40.1 25.2C36.5 26.3 32.9 27.4 29.6 28.4C31.7 18.8 39.8 4.3 49.1 4Z"
              fill="#95BF47"
            />
            <path
              d="M94.8 22.9C94.3 22.9 85.5 22.7 85.5 22.7C85.5 22.7 78.1 15.5 77.4 14.8C77.1 14.5 76.8 14.4 76.4 14.3V124L109.5 115.8C109.5 115.8 96 24.5 95.9 23.8C95.8 23.3 95.3 22.9 94.8 22.9Z"
              fill="#5E8E3E"
            />
            <path
              d="M58 39.9L54.2 54.3C54.2 54.3 49.9 52.3 44.8 52.7C37.3 53.2 37.3 57.9 37.3 59.1C37.7 65.5 54.6 66.9 55.6 82C56.3 93.9 49.3 102 39.2 102.6C27 103.4 20.3 96.2 20.3 96.2L22.9 85.2C22.9 85.2 29.6 90.3 35 89.9C38.5 89.7 39.8 86.8 39.7 84.8C39.2 76.4 25.4 76.9 24.5 63.1C23.8 51.5 31.4 39.7 48.2 38.7C54.7 38.2 58 39.9 58 39.9Z"
              fill="white"
            />
          </svg>
          <svg
            v-if="env.platform === 'shopify_plus'"
            width="25"
            height="28"
            class=""
            stroke="none"
            viewBox="0 0 75 85"
            fill="#000"
            xmlns="http://www.w3.org/2000/svg"
          ><path
            d="M50.823 9.83771C50.8164 9.8399 49.8806 10.1292 48.2982 10.6202C48.0352 9.76321 47.645 8.709 47.0905 7.65041C45.3021 4.23796 42.6809 2.42983 39.5161 2.42544C39.5117 2.42544 39.5095 2.42544 39.5051 2.42544C39.2859 2.42544 39.0668 2.44515 38.8476 2.46488C38.7556 2.3531 38.6613 2.24133 38.5605 2.13613C37.1819
          0.661122 35.411 -0.0577457 33.2939 0.00362146C29.2042 0.121972 25.132 3.07419 21.8314 8.31889C19.506 12.0097 17.7417 16.6473 17.2398 20.2351C12.5452 21.6882 9.25987 22.7073 9.18973 22.7292C6.82052 23.4722 6.746 23.5467 6.43478 25.78C6.20027 27.4698 0 75.4217 0 75.4217L51.4476 84.3199V9.73251C51.1934 9.75005 50.9654 9.79607 50.823 9.83771ZM38.944 13.5176C36.222 14.3614 33.25 15.2819 30.2672 16.2046C31.1066 12.9937 32.6955 9.79607 34.6483 7.70082C35.3738 6.92277 36.3907 6.05269 37.594 5.55518C38.7271 7.91343 38.9703 11.2579 38.944 13.5176ZM33.3728
          2.7257C34.3327 2.70598 35.1415 2.91637 35.8319 3.37005C34.7272 3.94427 33.6577 4.76835 32.6561 5.84227C30.059 8.6279 28.0689 12.9543 27.2755 17.1273C24.7989 17.8943 22.3771 18.6439 20.1503 19.3343C21.5552 12.7658 27.0607 2.90761 33.3728 2.7257ZM25.417 40.1465C25.6931 44.5233 37.2082 45.4789 37.8548 55.7316C38.3632 63.797 33.5766 69.3134 26.6794 69.7496C18.4014 70.2712 13.8449 65.3882 13.8449 65.3882L15.5982 57.9255C15.5982 57.9255 20.1854 61.3861 23.8565 61.1538C26.2542 61.0026 27.1111 59.052 27.0257
          57.6712C26.6662 51.9619 17.2902 52.2972 16.6963 42.9168C16.1987 35.0224 21.3821 27.0227 32.8227 26.2994C37.2302 26.0211 39.4876 27.1454 39.4876 27.1454L36.8707 36.9313C36.8707 36.9313 33.9536 35.6032 30.4929 35.8201C25.4192 36.1445 25.3644 39.3444 25.417 40.1465ZM41.6617 12.6738C41.631 10.6026 41.3834 7.72273 40.419 5.23298C43.5225 5.82035 45.0501 9.33143 45.6944 11.4245C44.5 11.7971 43.1389 12.2179 41.6617 12.6738Z"
          /><path d="M53.1484 84.1161L74.491 78.81C74.491 78.81 65.3013 16.6802 65.2443 16.2528C65.1851 15.8276 64.8125 15.5909 64.5057 15.5646C64.1989 15.5383 58.1893 15.4484 58.1893 15.4484C58.1893 15.4484 54.5248 11.8913 53.1484 10.5434V84.1161Z" /></svg>
        </div>
        <div
          v-if="env.platform === 'shopify' || env.platform === 'shopify_plus'"
          class=" inline-block align-middle h-full items-center text-sm text-gray-500"
        >
          <span class="mr-1 inline align-middle">SparkLayer works with all versions of Shopify, no
            matter which Shopify plan you're on.</span>
          <a
            href="https://docs.sparklayer.io/shopify"
            target="_blank"
            rel="noopener noreferrer"
            class="font-medium align-middle inline-flex items-center text-indigo-600 hover:text-indigo-500"
          >
            <span>Learn more</span>
            <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
          </a>
        </div>
      </div>
    </li>
  </ul>
</template>

<script>
import {
  ChevronDownIcon, ChevronRightIcon, ExternalLinkIcon, ArrowRightIcon,
} from '@heroicons/vue/outline';
import { mapGetters } from 'vuex';
import Btn from '@/components/Btn.vue';
import StatusIcon from '@/components/StatusIcon.vue';
import OnboardingStatusText from '@/components/StatusText.vue';
import api from '@/utils/api';
import SyncData from '@/components/Onboarding/SyncData.vue';
import FrontendHints from '@/components/FrontendHints.vue';
import { basicCannon, stars } from '@/utils/confetti';

export default {
  name: 'Onboarding',
  components: {
    SyncData,
    Btn,
    FrontendHints,
    OnboardingStatusText,
    StatusIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    ExternalLinkIcon,
    ArrowRightIcon,
  },
  data() {
    return {
      toggleSyncStore: false,
      toggleSyncData: false,
      syncLoading: false,
      toggleFrontend: false,
      installLoading: false,
      installFlags: {
        core: false,
        pdp: false,
        css: false,
        checkout: false,
        advanced: false,
      },
      pricingLoading: false,
      pricingFlags: {
        one: false,
        two: false,
      },
      toggleSettings: false,
      settingsLoading: false,
      settingsFlags: {
        one: false,
        two: false,
        three: false,
      },
      toggleLaunch: false,
      launchLoading: false,
      launchFlags: {
        start: false,
        login: false,
        pricing: false,
        cart: false,
        checkout: false,
        orders: false,
        invite: false,
      },
      togglePlus: false,
    };
  },
  computed: {
    ...mapGetters({
      env: 'env/get',
      isAdvancedFeaturesEnabled: 'auth/isAdvancedFeaturesEnabled',
    }),
    installFlagsAllow() {
      return Object.keys(this.installFlags).every((k) => this.installFlags[k]);
    },
    pricingFlagsAllow() {
      return Object.keys(this.pricingFlags).every((k) => this.pricingFlags[k]);
    },
    settingsFlagsAllow() {
      return Object.keys(this.settingsFlags).every((k) => this.settingsFlags[k]);
    },
    launchFlagsAllow() {
      return Object.keys(this.launchFlags).every((k) => this.launchFlags[k]);
    },
    shopifyDomainCustomers() {
      if (!(this.env?.shopify_site_url)) {
        return '';
      }
      return `https://${this.env?.shopify_site_url}/admin/customers`;
    },
    shopifyDomainSettings() {
      if (!(this.env?.shopify_site_url)) {
        return '';
      }
      return `https://${this.env?.shopify_site_url}/admin/settings/general`;
    },
    shopifyDomainAccount() {
      if (!(this.env?.shopify_site_url)) {
        return '';
      }
      return `https://${this.env?.shopify_site_url}/account`;
    },
    shopifyDomainCheckout() {
      if (!(this.env?.shopify_site_url)) {
        return '';
      }
      return `https://${this.env?.shopify_site_url}/admin/settings/checkout`;
    },
    shopifyDomainDraftOrders() {
      if (!(this.env?.shopify_site_url)) {
        return '';
      }
      return `https://${this.env?.shopify_site_url}/admin/draft_orders`;
    },
    shopifyDomainAdmin() {
      if (!(this.env?.shopify_site_url)) {
        return '';
      }
      return `https://${this.env?.shopify_site_url}/admin`;
    },
  },
  mounted() {
    if (this.$route.query.expand) {
      this.expandSection(
        Array.isArray(this.$route.query.expand)
          ? this.$route.query.expand[0]
          : this.$route.query.expand,
      );
    }
  },
  methods: {
    async toggleSettingsChecks() {
      this.settingsLoading = true;
      await api.post(`/${this.env.environment}/toggle-onboarding-flag/settings`);
      window.Intercom('trackEvent', 'updated-onboarding-flag', {
        flag: 'settings',
        new_value: !this.env.onboarding_settings,
      });
      await this.$store.dispatch('env/loadEnvironment', this.env.environment);
      this.settingsLoading = false;
      if (this.env.onboarding_settings) {
        this.triggerConfetti();
      }
    },
    async toggleLaunchChecks() {
      this.launchLoading = true;
      await api.post(`/${this.env.environment}/toggle-onboarding-flag/launch`);
      window.Intercom('trackEvent', 'updated-onboarding-flag', {
        flag: 'launch',
        new_value: !this.env.onboarding_launch,
      });
      await this.$store.dispatch('env/loadEnvironment', this.env.environment);
      this.launchLoading = false;
      if (this.env.onboarding_launch) {
        this.triggerConfetti();
      }
    },
    async toggleInstallChecks() {
      this.installLoading = true;
      await api.post(`/${this.env.environment}/toggle-onboarding-flag/frontend`);
      window.Intercom('trackEvent', 'updated-onboarding-flag', {
        flag: 'frontend',
        new_value: !this.env.onboarding_frontend,
      });
      await this.$store.dispatch('env/loadEnvironment', this.env.environment);
      this.installLoading = false;
      if (this.env.onboarding_frontend) {
        this.triggerConfetti();
      }
    },
    async toggleSyncFlag() {
      this.syncLoading = true;
      await api.post(`/${this.env.environment}/toggle-onboarding-flag/sync`);
      window.Intercom('trackEvent', 'updated-onboarding-flag', {
        flag: 'sync',
        new_value: !this.env.onboarding_sync,
      });
      await this.$store.dispatch('env/loadEnvironment', this.env.environment);
      this.syncLoading = false;
      if (this.env.onboarding_sync) {
        this.triggerConfetti();
      }
    },
    triggerConfetti() {
      const allStepsComplete = this.env.onboarding_frontend
        && this.env.onboarding_launch
        && this.env.onboarding_platform
        && this.env.onboarding_settings
        && this.env.onboarding_sync;

      if (allStepsComplete) {
        stars();
      } else {
        basicCannon();
      }
    },
    expandSection(section) {
      switch (section) {
        case 'connect':
          this.toggleSyncStore = true;
          break;
        case 'pricing':
          this.toggleSyncData = true;
          break;
        case 'customer-groups':
          this.toggleSettings = true;
          break;
        case 'widgets':
          this.toggleFrontend = true;
          break;
        case 'launch':
          this.toggleLaunch = true;
          break;
        default:
      }

      this.$nextTick(() => { this.$refs[section].scrollIntoView(false); });
    },
  },
};
</script>
